/**
 * The module's gate, as a gql-only LEAF (PRO-130): the sidebar and the boot
 * prefetch in `src/App/router.tsx` import this file, so it imports nothing but
 * Apollo and generated types — never a component, never `src/antd`.
 *
 * `automationBootstrap` is read ONCE per session: the boot batch warms the
 * cache and every reader opts into `cache-first` (the app-wide default is
 * cache-and-network, PRO-131), so the gate costs no round trip on navigation.
 *
 * Fail-closed: while loading, on an error, or on a backend that has no engine,
 * every flag reads false (`NO_AUTOMATION_ACCESS`).
 */
import { ApolloClient, ApolloError, gql, useQuery } from '@apollo/client'

import type { AutomationBootstrap as AutomationBootstrapQuery } from './__generated__/types'

export const AUTOMATION_BOOTSTRAP = gql`
  query AutomationBootstrap {
    automationBootstrap {
      enabled
      paused
      pausedAt
      pausedBy {
        id
        firstName
        lastName
      }
      pauseReason
      heldEvents
      canView
      canCreate
      canEdit
      canDelete
      canDecideApprovals
    }
  }
`

type Answer = AutomationBootstrapQuery['automationBootstrap']

export type AutomationAccess = Omit<
  Answer,
  '__typename' | 'pausedAt' | 'pausedBy' | 'pauseReason' | 'heldEvents'
>

/** When, by whom and why the engine was paused, and how many events wait
 * (empty while it runs). */
export type AutomationPause = Pick<
  Answer,
  'pausedAt' | 'pausedBy' | 'pauseReason' | 'heldEvents'
>

const NOT_PAUSED: AutomationPause = {
  pausedAt: undefined,
  pausedBy: undefined,
  pauseReason: undefined,
  heldEvents: 0,
}

export const NO_AUTOMATION_ACCESS: AutomationAccess = {
  enabled: false,
  paused: false,
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  canDecideApprovals: false,
}

export type AutomationBootstrapState = {
  /** The bootstrap has not answered yet: render nothing that decides access. */
  loading: boolean
  /** The bootstrap failed (network or GraphQL); flags are fail-closed. */
  error: ApolloError | undefined
  /** The answer, or `NO_AUTOMATION_ACCESS` until/unless there is one. */
  access: AutomationAccess
  /** Who paused the engine, when and why; empty unless `access.paused`. */
  pause: AutomationPause
  /** True once an answer arrived. */
  ready: boolean
  refetch: () => void
}

export const useAutomationBootstrap = (): AutomationBootstrapState => {
  const { data, loading, error, refetch } = useQuery<AutomationBootstrapQuery>(
    AUTOMATION_BOOTSTRAP,
    { fetchPolicy: 'cache-first', notifyOnNetworkStatusChange: true }
  )
  const answer = data?.automationBootstrap
  const access: AutomationAccess = answer
    ? {
        enabled: answer.enabled,
        paused: answer.paused,
        canView: answer.canView,
        canCreate: answer.canCreate,
        canEdit: answer.canEdit,
        canDelete: answer.canDelete,
        canDecideApprovals: answer.canDecideApprovals,
      }
    : NO_AUTOMATION_ACCESS
  return {
    pause: answer
      ? {
          pausedAt: answer.pausedAt,
          pausedBy: answer.pausedBy,
          pauseReason: answer.pauseReason,
          heldEvents: answer.heldEvents,
        }
      : NOT_PAUSED,
    loading: loading && !answer,
    error: answer ? undefined : error,
    access,
    ready: Boolean(answer),
    refetch: () => {
      refetch().catch(() => undefined)
    },
  }
}

/** Where the sidebar sends this user, if anywhere. */
export type AutomationEntry = 'module' | 'approvals' | null

export const automationEntryFor = (
  access: AutomationAccess
): AutomationEntry => {
  if (!access.enabled) return null
  if (access.canView) return 'module'
  if (access.canDecideApprovals) return 'approvals'
  return null
}

/** Fire-and-forget boot prefetch (the DASHBOARD_CONFIGS shape): warms the
 * cache so `useAutomationBootstrap` reads it cache-first. A failure is
 * ignored — the hook makes its own request and fails closed. */
export const prefetchAutomationBootstrap = (client: ApolloClient<object>) =>
  client.query({ query: AUTOMATION_BOOTSTRAP }).catch(() => undefined)
